<template>
    <v-card>
        <v-card-title>
            <span v-if="data && data.code_f && data.customer">{{ data.code_f }} - {{ data.customer.name }}</span>
            <v-spacer></v-spacer>
        </v-card-title>
        <v-card-text>
            <div class="d-flex align-center justify-center" v-if="loading">
                <v-progress-circular indeterminate color="primary"/>
            </div>
            <div v-else>
                <v-row>
                    <v-col>
                        <v-subheader>General Information</v-subheader>
                        <v-row>
                            <v-col>
                                <v-simple-table dense>
                                    <tbody>
                                        <tr>
                                            <th>Date</th>
                                            <td>
                                                <div class="d-flex align-center">
                                                    <span>
                                                        {{ formatDate(data.start_at) }}
                                                    </span>
                                                    <v-spacer></v-spacer>
                                                    <v-btn v-if="hasPermission('SASCHU')" depressed small icon class="ms-1" @click="changeDateDialog = true">
                                                        <v-icon small>mdi-pencil</v-icon>
                                                    </v-btn>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>State</th>
                                            <td>
                                                <div v-if="!changeState" class="d-flex align-center">
                                                    <div>
                                                        <v-badge inline left dot :color="renderColor(data.state_id)"></v-badge>
                                                        <span v-if="data.state">{{data.state.name}}</span>
                                                    </div>
                                                    <v-spacer></v-spacer>
                                                    <v-btn v-if="hasPermission('SASCHU')" small depressed icon class="ms-2" @click="changeState = true">
                                                        <v-icon small>mdi-pencil</v-icon>
                                                    </v-btn>
                                                </div>
                                                <div v-else class="d-flex align-center">
                                                    <SchedulingStateSelect
                                                        label="State"
                                                        v-model="updatedStateId"
                                                        item-text="name"
                                                        item-value="id"
                                                        dense
                                                        single-line
                                                        hide-details="auto"
                                                    />
                                                    <v-btn small depressed icon class="ms-1" @click="onChangeState" color="success" :loading="changeStateLoading">
                                                        <v-icon small>mdi-check-bold</v-icon>
                                                    </v-btn>
                                                    <v-btn small depressed icon @click="changeState = false">
                                                        <v-icon small>mdi-close</v-icon>
                                                    </v-btn>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Priority</th>
                                            <td v-if="data.priority">{{ data.priority.name }}</td>
                                        </tr>
                                        <tr>
                                            <th>Customer</th>
                                            <td class="d-flex align-center">
                                                <span v-if="data.customer" class="font-weight-bold">{{ data.customer.code_f }} - {{ data.customer.name }}</span>
                                                <v-spacer></v-spacer>
                                                <v-btn small depressed @click="handleOpenCustomerCenter">
                                                    <v-icon small left>mdi-magnify</v-icon>
                                                    center
                                                </v-btn> 
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Item</th>
                                            <td>
                                                <div v-if="!changeItem" class="d-flex align-center">
                                                    <span v-if="data.item">{{ data.item.name }}</span>
                                                    <v-spacer></v-spacer>
                                                    <v-btn v-if="hasPermission('SASCHU')" small depressed icon class="ms-2" @click="changeItem = true">
                                                        <v-icon small>mdi-pencil</v-icon>
                                                    </v-btn>
                                                </div>
                                                <div v-else class="d-flex align-center">
                                                    <v-autocomplete
                                                        label="Item"
                                                        dense
                                                        hide-details    
                                                        single-line
                                                        :items="items"
                                                        v-model="updatedItemId"
                                                        item-text="name"
                                                        item-value="id"
                                                        :loading="itemsLoading"
                                                        @update:search-input="searchItems"
                                                    >
                                                    </v-autocomplete>
                                                    <v-btn small depressed icon class="ms-1" @click="onChangeItem" color="success">
                                                        <v-icon small>mdi-check-bold</v-icon>
                                                    </v-btn>
                                                    <v-btn small depressed icon @click="changeItem = false">
                                                        <v-icon small>mdi-close</v-icon>
                                                    </v-btn>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Quantity</th>
                                            <td class="font-weight-bold">
                                                <div v-if="!changeQuantity" class="d-flex align-center">
                                                    <span v-if="data.measure_unit">{{ formatNumber(data.quantity) }} {{ data.measure_unit.abbreviation }}</span>
                                                    <v-spacer></v-spacer>
                                                    <v-btn v-if="hasPermission('SASCHU')" small depressed icon class="ms-2" @click="changeQuantity = true">
                                                        <v-icon small>mdi-pencil</v-icon>
                                                    </v-btn>
                                                </div>
                                                <div v-else class="d-flex align-center">
                                                    <v-text-field
                                                        dense 
                                                        hide-details
                                                        v-model="updatedQuantity"
                                                        :suffix="data.measure_unit.abbreviation"
                                                        :loading="changeQuantityLoading"
                                                        >
                                                    </v-text-field>
                                                    <v-btn small depressed icon class="ms-1" @click="onChangeQuantity" color="success">
                                                        <v-icon small>mdi-check-bold</v-icon>
                                                    </v-btn>
                                                    <v-btn small depressed icon @click="changeQuantity = false">
                                                        <v-icon small>mdi-close</v-icon>
                                                    </v-btn>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Cast Type</th>
                                            <td>
                                                <div v-if="!changeCastType" class="d-flex align-center">
                                                    <span v-if="data.cast_type">{{ data.cast_type.name }}</span>
                                                    <v-spacer></v-spacer>
                                                    <v-btn v-if="hasPermission('SASCHU')" small depressed icon class="ms-2" @click="changeCastType = true">
                                                        <v-icon small>mdi-pencil</v-icon>
                                                    </v-btn>
                                                </div>
                                                <div v-else class="d-flex align-center">
                                                    <v-select
                                                        label="Cast Type"
                                                        v-model="updatedCastTypeId"
                                                        item-text="name"
                                                        item-value="id"
                                                        :items="castTypes"
                                                        dense
                                                        single-line
                                                        hide-details="auto"
                                                    />
                                                    <v-btn small depressed icon class="ms-1" @click="onChangeCastType" color="success" :loading="changeCastTypeLoading">
                                                        <v-icon small>mdi-check-bold</v-icon>
                                                    </v-btn>
                                                    <v-btn small depressed icon @click="changeCastType = false">
                                                        <v-icon small>mdi-close</v-icon>
                                                    </v-btn>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Site</th>
                                            <td>
                                                <div v-if="!changeSite" class="d-flex align-center">
                                                    <span v-if="data.site">{{ data.site.name }}</span>
                                                    <v-spacer></v-spacer>
                                                    <v-btn v-if="hasPermission('SASCHU')" small depressed icon class="ms-2" @click="changeSite = true">
                                                        <v-icon small>mdi-pencil</v-icon>
                                                    </v-btn>
                                                </div>
                                                <div v-else class="d-flex align-center">
                                                    <v-autocomplete
                                                        label="Site"
                                                        dense
                                                        hide-details    
                                                        single-line
                                                        :items="sites"
                                                        v-model="updatedSiteId"
                                                        item-text="name"
                                                        item-value="id"
                                                        :loading="sitesLoading"
                                                    >
                                                    </v-autocomplete>
                                                    <v-btn small depressed icon class="ms-1" @click="onChangeSite" color="success">
                                                        <v-icon small>mdi-check-bold</v-icon>
                                                    </v-btn>
                                                    <v-btn small depressed icon @click="changeSite = false">
                                                        <v-icon small>mdi-close</v-icon>
                                                    </v-btn>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Address</th>
                                            <td v-if="data.site">
                                                <span>{{ data.site.address }}</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Floor</th>
                                            <td>
                                                <div v-if="!changeFloor" class="d-flex align-center">
                                                    <span v-if="data.floor">{{ data.floor }}</span>
                                                    <v-spacer></v-spacer>
                                                    <v-btn v-if="hasPermission('SASCHU')" small depressed icon class="ms-2" @click="changeFloor = true">
                                                        <v-icon small>mdi-pencil</v-icon>
                                                    </v-btn>
                                                </div>
                                                <div v-else class="d-flex align-center">
                                                    <v-select
                                                        label="Floor"
                                                        dense
                                                        hide-details    
                                                        single-line
                                                        :items="floors"
                                                        v-model="updatedFloorId"
                                                    />
                                                    <v-btn small depressed icon class="ms-1" @click="onChangeFloor" color="success" :loading="changeFloorLoading">
                                                        <v-icon small>mdi-check-bold</v-icon>
                                                    </v-btn>
                                                    <v-btn small depressed icon @click="changeFloor = false">
                                                        <v-icon small>mdi-close</v-icon>
                                                    </v-btn>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Plant</th>
                                            <td>
                                                <div v-if="!changePlant" class="d-flex align-center">
                                                    <span v-if="data.plant">{{ data.plant.name }}</span>
                                                    <span v-else>--</span>
                                                    <v-spacer></v-spacer>
                                                    <v-btn v-if="hasPermission('SASCHU')" small depressed icon class="ms-2" @click="changePlant = true">
                                                        <v-icon small>mdi-pencil</v-icon>
                                                    </v-btn>
                                                </div>
                                                <div v-else class="d-flex align-center">
                                                    <v-select
                                                        label="Plant"
                                                        dense
                                                        hide-details    
                                                        single-line
                                                        :items="plants"
                                                        v-model="updatedPlantId"
                                                        item-text="name"
                                                        item-value="id"
                                                        :loading="plantsLoading"
                                                    />
                                                    <v-btn small depressed icon class="ms-1" @click="onChangePlant" color="success" :loading="changePlantLoading">
                                                        <v-icon small>mdi-check-bold</v-icon>
                                                    </v-btn>
                                                    <v-btn small depressed icon @click="changePlant = false">
                                                        <v-icon small>mdi-close</v-icon>
                                                    </v-btn>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Remark</th>
                                            <td>{{ data.remark }}</td>
                                        </tr>
                                    </tbody>
                                </v-simple-table>
                            </v-col>
                        </v-row>
                        <v-subheader>Waiting List</v-subheader>
                        <v-row>
                            <v-col>
                                <v-simple-table dense>
                                    <tbody>
                                        <tr>
                                            <th>Preferred Date</th>
                                            <td>
                                                <div v-if="!changeWaitingList" class="d-flex align-center">
                                                    <span>{{data.ready_date ? formatDate(data.ready_date) : "Not Set"}}</span>
                                                    <v-spacer></v-spacer>
                                                    <v-btn icon depressed small class="ms-1" @click="changeWaitingList = true">
                                                        <v-icon small>mdi-pencil</v-icon>
                                                    </v-btn>
                                                </div>
                                                <div v-else class="d-flex align-center">
                                                    <DatePicker
                                                        label="Waiting List Date"
                                                        single-line
                                                        dense
                                                        hide-details
                                                        v-model="updatedWaitingListDate"
                                                        append-icon="mdi-clock"
                                                    ></DatePicker>
                                                    <v-btn depressed small icon class="ms-1" @click="onChangeWaitingList" color="success" :loading="changeWaitingListLoading">
                                                        <v-icon small >mdi-check-bold</v-icon>
                                                    </v-btn>
                                                    <v-btn depressed small icon @click="changeWaitingList = false">
                                                        <v-icon small>mdi-close</v-icon>
                                                    </v-btn>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </v-simple-table>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col>
                                <v-simple-table dense>
                                    <tbody>
                                        <tr>
                                            <th>Created By</th>
                                            <td>
                                                <span v-if="data.created_by">{{ data.created_by.first_name }} {{ data.created_by.last_name }}</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Created At</th>
                                            <td>{{ data.created_at }}</td>
                                        </tr>
                                    </tbody>
                                </v-simple-table>
                            </v-col>
                        </v-row>
                    </v-col>
                    <v-col>
                        <v-subheader>Activities</v-subheader>
                        <v-row>
                            <v-col>
                                <div style="max-height:350px; overflow-y: auto;" ref="commentsContainer" v-if="!activitiesFetchLoading">
                                    <div class="mb-3 pa-2 grey lighten-4" v-for="(d, i) in activities" :key="i">
                                        <div class="d-flex flex-column">
                                            <div class="d-flex align-center">
                                                <div class="font-weight-bold primary--text me-2">{{ d.user.first_name }} {{ d.user.last_name }}</div>
                                                <div class="text-caption">{{ d.created_at }}</div>
                                                <v-icon small class="ms-1" v-if="d.is_generated">mdi-cog</v-icon>
                                                <v-icon small class="ms-1" v-else>mdi-chat</v-icon>
                                            </div>
                                            <div class="text-body-2">{{d.activity}}</div>
                                        </div>
                                    </div>
                                </div>
                                <div v-else>
                                    <v-progress-circular indeterminate size="50" color="primary"/>
                                </div>
                                <v-text-field
                                    hide-details
                                    append-icon="mdi-send"
                                    v-model="activity.activity"
                                    @click:append="storeActivityAsync"
                                    @keypress.enter.ctrl="storeActivityAsync"
                                    placeholder="Write Activity then (Crt + Enter)"
                                    :loading="activityStoreLoading"
                                    outlined
                                    dense
                                    class="mt-2">
                                </v-text-field>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
            </div>
        </v-card-text>
        <v-card-actions v-if="!loading">
            <v-btn depressed @click="$emit('close')">close</v-btn>
        </v-card-actions>
        <v-dialog v-model="changeDateDialog" max-width="800">
            <SchedulingChangeDate
                v-if="changeDateDialog"
                @cancel="changeDateDialog = false"
                @submit="onSubmitChangeDate"
                :date="data.start_at"
                :scheduleId="data.id"
            />
        </v-dialog>
        <CustomerDisplayDialog ref="customerDisplayDialog"/>
    </v-card>
</template>

<script>
import { 
    getSalesSchedule, 
    updateSalesScheduleQuantity, 
    updateSalesScheduleItem, 
    updateSalesScheduleState, 
    updateSalesScheduleWaitingList, 
    updateSalesScheduleCastType, 
    updateSalesSchedulePlant,
    updateSalesScheduleSite,
    updateSalesScheduleFloor
} from './../../services/scheduling/schedule';
import { states } from './../../data/sales/scheduling/states';
import { formatNumber } from "accounting";
import moment from "moment";
import SchedulingChangeDate from './SchedulingChangeDate.vue';
import activityAPI from './../../services/scheduling/activity';
import itemAPI from './../../services/scheduling/item';
import SchedulingStateSelect from './SchedulingStateSelect.vue';
import DatePicker from '../common/DatePicker.vue';
import authorize from '../../utils/authorize';
import CustomerDisplayDialog from './CustomerDisplayDialog.vue';
import castTypeService from '../../services/scheduling/castType';
import plantService from '../../services/scheduling/plant';
import siteService from '../../services/scheduling/site';

export default {
    components: {
    SchedulingChangeDate,
    SchedulingStateSelect,
    DatePicker,
    CustomerDisplayDialog
},
    props: {
        eventId: [Number, String]
    },
    data() {
        return {
            data: {},
            loading: false,
            changeDateDialog: false,
            activity: {
                schedule_id: this.eventId,
                activity: ""
            },
            activities: [],
            activitiesFetchLoading: false,
            activityStoreLoading: false,

            // change quantity
            changeQuantity: false,
            changeQuantityLoading: false,
            updatedQuantity: 0,

            // change item
            changeItem: false,
            changeItemLoading: false,
            items: [],
            itemsLoading: false,
            updatedItemId: null,

            // change state
            changeState: false,
            states: [],
            updatedStateId: null,
            changeStateLoading: false,

            // change waiting list
            changeWaitingList: false,
            changeWaitingListLoading: false,
            updatedWaitingListDate: null,

            // change cast type
            changeCastType: false,
            updatedCastTypeId: null,
            castTypes: [],
            changeCastTypeLoading: false,
            castTypesLoading: false,

            // change plant
            plants: [],
            plantsLoading: false,
            updatedPlantId: null,
            changePlant: false,
            changePlantLoading: false,

            // change site
            changeSite: false,
            changeSiteLoading: false,
            updatedSiteId: null,
            sites: [],
            sitesLoading: false,

            // change floor
            changeFloor: false,
            changeFloorLoading: false,
            updatedFloorId: null,
            floors: [
                { text: "Basement", value: -1 },
                { text: "Ground Floor", value: 0 },
                { text: "1st Floor", value: 1 },
                { text: "2nd Floor", value: 2 },
                { text: "3rd Floor", value: 3 },
                { text: "4th Floor", value: 4 },
                { text: "5th Floor", value: 5 },
                { text: "6th Floor", value: 6 },
                { text: "7th Floor", value: 7 },
                { text: "8th Floor", value: 8 },
                { text: "9th Floor", value: 9 },
                { text: "10th Floor", value: 10 },
                { text: "11th Floor", value: 11 },
                { text: "12th Floor", value: 12 },
                { text: "13th Floor", value: 13 },
                { text: "14th Floor", value: 14 },
                { text: "15th Floor", value: 15 },
                { text: "16th Floor", value: 16 },
                { text: "17th Floor", value: 17 },
                { text: "18th Floor", value: 18 },
                { text: "19th Floor", value: 19 },
                { text: "20th Floor", value: 20 },
            ],
        }
    },
    computed: {
        
    },
    methods: {
        hasPermission(code) {
            return authorize(code);
        },
        formatNumber,
        formatDate(date) {
            return moment(date).format("YYYY-MM-DD")
        },
        handleClose() {
            this.$emit("close");
        },
        fetch() {
            this.loading = true;
            getSalesSchedule(this.eventId).then((res) => {
                this.data = res.data;
                this.updatedQuantity = res.data.quantity;
                this.updatedItemId = res.data.item_id;
                this.updatedStateId = res.data.state_id;
                this.updatedWaitingListDate = res.data.ready_date;
                this.updatedCastTypeId = res.data.cast_type_id;
                this.updatedPlantId = res.data.plant_id;
                this.updatedSiteId = res.data.site_id;
                this.updatedFloorId = res.data.floor;
                this.fetchSites();
            }).finally(() => {
                this.loading = false;
            })
        },
        renderColor(stateId) {
            let state = states.find(each => each.id == stateId);
            return state ? state.color : "black";
        },
        storeActivityAsync() {
            if(this.activity.activity.trim() == "") {
                return
            }

            this.activity = {
                ...this.activity,
                activity: this.activity.activity.trim()
            }
            this.activityStoreLoading = true;
            activityAPI.store(this.activity).then(res => {
                this.activities.push(res.data);
                this.activity = {
                    ...this.activity,
                    activity: ""
                }
            }).finally(() => {
                this.activityStoreLoading = false;
            })
        },
        fetchActivitiesAsync() {
            this.activitiesFetchLoading = true;
            activityAPI.all(this.eventId).then(res => {
                this.activities = res.data;
            }).finally(() => {
                this.activitiesFetchLoading = false
            })
        },
        scrollToEnd() {
            let container = this.$refs.commentsContainer;
            if(container) {
                container.scrollTop = container.scrollHeight + 100;
            }
        },
        onSubmitChangeDate() {
            this.fetch();
            this.fetchActivitiesAsync();
            this.changeDateDialog = false;
            this.$emit("updated");
        },
        onChangeQuantity() {
            this.changeQuantityLoading = true;
            updateSalesScheduleQuantity(this.eventId, this.updatedQuantity).then(() => {
                this.fetch();
                this.fetchActivitiesAsync();
                this.$emit("updated");
            }).finally(() => {
                this.changeQuantity = false;
                this.changeQuantityLoading = false;
            })
        },
        onChangeItem() {
            this.changeItemLoading = true;
            updateSalesScheduleItem(this.eventId, this.updatedItemId).then(() => {
                this.fetch();
                this.fetchActivitiesAsync();
                this.$emit("updated");
            }).finally(() => {
                this.changeItem = false;
                this.changeItemLoading = false;
            })
        },
        onChangeState() {
            this.changeStateLoading = true;
            updateSalesScheduleState(this.eventId, this.updatedStateId).then(() => {
                this.fetch();
                this.fetchActivitiesAsync();
                this.$emit("updated");
            }).finally(() => {
                this.changeState = false;
                this.changeStateLoading = false;
            })
        },
        onChangeWaitingList() {
            this.changeWaitingListLoading = true;
            updateSalesScheduleWaitingList(this.eventId, this.updatedWaitingListDate).then(() => {
                this.fetch();
                this.fetchActivitiesAsync();
                this.$emit("updated");
            }).finally(() => {
                this.changeWaitingList = false;
                this.changeWaitingListLoading = false;
            })
        },
        searchItems(query) {
            this.itemsLoading = true;
            itemAPI.search(query).then(res => {
                this.items = [...this.items, ...res.data, this.data.item]
            }).finally(() => {
                this.itemsLoading = false;
            })
        },
        handleOpenCustomerCenter() {
            this.$refs.customerDisplayDialog.open({ id: this.data.customer_id });
        },
        onChangeCastType() {
            this.changeCastTypeLoading = true;
            updateSalesScheduleCastType(this.eventId, this.updatedCastTypeId).then(() => {
                this.fetch();
                this.fetchActivitiesAsync();
                this.$emit("updated");
            }).finally(() => {
                this.changeCastType = false;
                this.changeCastTypeLoading = false;
            })
        },
        fetchCastTypes() {
            this.castTypesLoading = true;
            castTypeService.all().then(res => {
                this.castTypes = res.data;
            }).finally(() => {
                this.castTypesLoading = false;
            })
        },
        fetchPlants() {
            this.plantsLoading = true;
            plantService.all().then(res => {
                this.plants = res.data;
            }).finally(() => {
                this.plantsLoading = false;
            })
        },
        onChangePlant() {
            this.changePlantLoading = true;
            updateSalesSchedulePlant(this.eventId, this.updatedPlantId).then(() => {
                this.fetch();
                this.fetchActivitiesAsync();
                this.$emit("updated");
            }).finally(() => {
                this.changePlant = false;
                this.changePlantLoading = false;
            })
        },
        onChangeSite() {
            this.changeSiteLoading = true;
            updateSalesScheduleSite(this.eventId, this.updatedSiteId).then(() => {
                this.fetch();
                this.fetchActivitiesAsync();
                this.$emit("updated");
            }).finally(() => {
                this.changeSite = false;
                this.changeSiteLoading = false;
            })
        },
        fetchSites() {
            this.sitesLoading = true;
            siteService.all(this.data.customer_id).then(res => {
                this.sites = res.data;
            }).finally(() => {
                this.sitesLoading = false;
            })
        },
        onChangeFloor() {
            this.changeFloorLoading = true;
            updateSalesScheduleFloor(this.eventId, this.updatedFloorId).then(() => {
                this.fetch();
                this.fetchActivitiesAsync();
                this.$emit("updated");
            }).finally(() => {
                this.changeFloor = false;
                this.changeFloorLoading = false;
            })
        },
    },
    async created() {
        this.fetchActivitiesAsync();
        this.fetchCastTypes();
        this.fetchPlants();
        this.fetch();
    },
    updated() {
        this.scrollToEnd();
    }
}
</script>

<style>

</style>